import {
    mapZZJInformationRequest,
    stepperStateSelector,
    subListSetSize,
    subStepsSelector,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import { FC, useEffect, useMemo, useState } from "react";
import { PdfInStepper } from "../PdfViewers";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import { PensionQuestionnaireStepProps } from "./PensionQuestionnaireStepper/PensionQuestionnaireStepper";

type PensionQuestionnairePdfProps = {
    width?: number;
} & DefaultStepProps &
    PensionQuestionnaireStepProps;

const PDF_PAGE_WIDTH = 612;
const PDF_PAGE_PADDING = 32;

export const PensionQuestionnairePdf: FC<PensionQuestionnairePdfProps> = ({
    width,
    queries: { questionnaireConfigQuery, questionnairePdfMutation },
    setContinueCondition,
    setNextButtonDisabled,
    contractId,
}) => {
    const dispatch = useAppDispatch();
    const { subStepIndex, subStepsLength } = useAppSelector(subStepsSelector);
    const stepperState = useAppSelector(stepperStateSelector);
    const { data: config, refetch: refetchConfig } = questionnaireConfigQuery ?? {};
    const { mutateAsync: generatePdf } = questionnairePdfMutation ?? {};
    const [pdfData, setPdfData] = useState<ArrayBuffer | undefined>(undefined);

    const zzjInformation = useMemo(() => {
        if (!config) return null;
        return mapZZJInformationRequest(stepperState, config);
    }, [stepperState, config]);

    // Fetch config for first load
    useEffect(() => {
        if (!config) refetchConfig?.();
    }, [refetchConfig, config]);

    // Fetch PDF when we have the necessary data
    useEffect(() => {
        const fetchPdf = async () => {
            if (zzjInformation && contractId && generatePdf && !pdfData) {
                const blob = await generatePdf({ contractId, zZJInformationRequest: zzjInformation });
                if (!blob) return;
                // Ensure we have a proper Blob (axios may return ArrayBuffer or other types in some environments)
                const actualBlob = blob instanceof Blob ? blob : new Blob([blob]);
                setPdfData(await actualBlob.arrayBuffer());
            }
        };
        fetchPdf();
    }, [contractId, generatePdf, pdfData, zzjInformation]);

    // Set continue condition to only allow proceeding when PDF is loaded
    useEffect(() => {
        setContinueCondition?.(async () => {
            return !!pdfData;
        });
    }, [setContinueCondition, pdfData]);

    const handlePageCountChange = (numPages: number) => {
        if (numPages !== subStepsLength) {
            // inform stepper about the number of pages (sub-steps)
            dispatch(subListSetSize({ size: numPages, subStepIndex, subStepsLength }));
        }
    };

    const scale = useMemo(() => {
        if (width) {
            return (width - PDF_PAGE_PADDING) / PDF_PAGE_WIDTH;
        }
        return undefined;
    }, [width]);

    return (
        <PdfInStepper
            key={subStepIndex}
            setNextButtonDisabled={setNextButtonDisabled}
            onPageCountChange={handlePageCountChange}
            fileContent={pdfData}
            page={subStepIndex}
            scale={scale}
        />
    );
};
